/**
 * "Left for you to name" on Payees' Items to review tab (TMMAPP-0164 item 12; TMMAPI-0195 U3): the
 * bank texts nobody has named, one row per naming key, from `GET /v1/me/payees/unnamed`, 50 at a
 * time with **Show more** (never infinite scroll). It is not an item to review: nothing here counts in
 * the tab's badge or the sidebar, so a tab with only this section doesn't open by default. Its heading
 * carries the served `total` and takes focus when the coverage card's **Name them** asks
 * (`focusToken`).
 *
 * Each row, in the "Names to review" style: "From your bank" and the text; "{n} transactions · last
 * {date}" (plain text: the contract has no lines read by key); the newest line's date, amount (in its
 * own currency, a register amount, visual contract v2 §13) and account; "AI had no suggestion" when
 * AI looked. Its actions: **Use "{suggested}"** when a proposal stands, **Name it** (the shared payee
 * picker, with a Kind for a new name only, Person when the hint says so), and **Keep unnamed**. At
 * `xs` the actions wrap under the text; from `md` they sit at the row's end.
 *
 * A name removes the row, says how many lines it named (and skipped), refreshes the coverage card in
 * place (`onChanged`) and moves focus to the next row's first action. A `404 PAYEE_TEXT_NOT_FOUND`
 * (named or gone meanwhile) removes the row quietly and refreshes. **Kept unnamed ({n})** at the foot
 * lists the kept texts with Restore. A viewer who can't write sees the rows without actions; a GUEST
 * or NONE tier's actions open the purchase (`useSubscriberAction`). With nothing to name the section
 * isn't drawn, though the kept link stays while texts are kept, so a restore is always reachable.
 */
import { useCallback, useEffect, useId, useRef, useState, type MouseEvent } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
  type Theme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';
import type { PayeeKind } from '@/api/payees';
import {
  unnamedPayeesApi,
  type UnnamedPayeeGroup,
  type UnnamedPayeeNameBody,
  type UnnamedPayeePage,
} from '@/api/unnamedPayees';
import { ErrorState } from '@/components/ErrorState';
import { MoneyText } from '@/components/MoneyText';
import { SubmitButton } from '@/components/SubmitButton';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import type { NotifyOptions } from '@/shell/NotifyProvider';
import { KeptUnnamedDialog } from './KeptUnnamedDialog';
import { PayeePicker, type PayeePick } from './PayeePicker';

const KIND_KEYS = {
  VENDOR: 'naming.unnamed.kind.VENDOR',
  PERSON: 'naming.unnamed.kind.PERSON',
  OTHER: 'naming.unnamed.kind.OTHER',
} as const satisfies Record<PayeeKind, string>;
const KINDS = Object.keys(KIND_KEYS) as PayeeKind[];

export interface UnnamedPayeesSectionProps {
  /** Bump to re-read in place (a run ended, a suggestion was decided). */
  reloadToken?: number | undefined;
  /** The texts kept unnamed (`naming-status.bySource.KEPT_UNNAMED`); null while unknown. */
  keptCount: number | null;
  /** A viewer who can't write: the rows without actions. */
  readOnly?: boolean | undefined;
  /** Bump to move focus to the heading (the coverage card's Name them). */
  focusToken?: number | undefined;
  /** A name, keep or restore changed the counts: the host refreshes the coverage card in place. */
  onChanged?: (() => void) | undefined;
  onNotify?: ((message: string, options?: NotifyOptions) => void) | undefined;
}

type State =
  | { status: 'loading' }
  | { status: 'ready'; page: UnnamedPayeePage }
  | { status: 'error'; error: unknown };

export function UnnamedPayeesSection({
  reloadToken = 0,
  keptCount,
  readOnly = false,
  focusToken = 0,
  onChanged,
  onNotify,
}: UnnamedPayeesSectionProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const headingId = useId();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [pick, setPick] = useState<PayeePick | null>(null);
  const [kind, setKind] = useState<PayeeKind>('VENDOR');
  const [keptOpen, setKeptOpen] = useState(false);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const rowRefs = useRef(new Map<string, HTMLElement>());
  // The key whose first action takes focus once the list re-renders (after a row left).
  const focusKey = useRef<string | null>(null);
  // How many rows are on screen: a re-read of page 1 reads up to as many (TMMAPP-0166 item 2).
  const shownCount = useRef(0);
  // A page-1 re-read (with its cursor follow) is in flight: the next-page read waits it out.
  const reloading = useRef(false);
  // Counts settled page-1 reads, either way: a next-page read skipped while one was in flight runs
  // when it settles, a failed one too (TMMAPP-0167 item 2).
  const [loadsSettled, setLoadsSettled] = useState(0);

  const load = useCallback(async () => {
    reloading.current = true;
    // The skeleton is the first load's only: a reload keeps the rows until the new page arrives.
    setState((prev) => (prev.status === 'ready' ? prev : { status: 'loading' }));
    try {
      const want = shownCount.current;
      let page = await unnamedPayeesApi.list();
      // A re-read after Show more keeps the rows already shown: the pages after it, up to as many.
      while (page.groups.length < want && page.nextCursor !== null) {
        page = appendPage(page, await unnamedPayeesApi.list({ cursor: page.nextCursor }));
      }
      setState({ status: 'ready', page });
    } catch (error) {
      setState((prev) => (prev.status === 'ready' ? prev : { status: 'error', error }));
    } finally {
      reloading.current = false;
      setLoadsSettled((n) => n + 1);
    }
  }, []);

  useEffect(() => {
    queueMicrotask(() => {
      void load();
    });
  }, [load, reloadToken]);

  const groups = state.status === 'ready' ? state.page.groups : [];
  const total = state.status === 'ready' ? state.page.total : 0;
  const nextCursor = state.status === 'ready' ? state.page.nextCursor : null;
  // Drawn while anything is left to name, shown or on a page not read yet (TMMAPP-0166 item 2).
  const drawn = state.status === 'ready' && (total > 0 || nextCursor !== null);
  useEffect(() => {
    shownCount.current = groups.length;
  });

  // The coverage card's Name them: the heading takes focus once it is on screen.
  const handledFocus = useRef(0);
  useEffect(() => {
    if (focusToken === 0 || focusToken === handledFocus.current || !drawn) return;
    handledFocus.current = focusToken;
    headingRef.current?.focus();
  }, [focusToken, drawn]);

  useEffect(() => {
    const key = focusKey.current;
    if (key === null) return;
    focusKey.current = null;
    const row = rowRefs.current.get(key);
    const target = row?.querySelector<HTMLElement>('[data-unnamed-action]');
    if (target) target.focus();
    else headingRef.current?.focus();
  });

  async function showMore() {
    if (state.status !== 'ready' || state.page.nextCursor === null || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await unnamedPayeesApi.list({ cursor: state.page.nextCursor });
      setState((prev) =>
        prev.status !== 'ready' ? prev : { status: 'ready', page: appendPage(prev.page, next) },
      );
    } catch (error) {
      onNotify?.(errorCopy(t, error, 'naming.unnamed.loadFailed'), { severity: 'error' });
    } finally {
      setLoadingMore(false);
    }
  }

  // The last shown row left with more pages unread: the next page is read in place, no skeleton
  // (TMMAPP-0166 item 2). Skipped while a page-1 re-read is in flight, it is checked again when that
  // read settles, so a failed re-read never leaves the section empty (TMMAPP-0167 item 2).
  const exhausted = state.status === 'ready' && groups.length === 0 && nextCursor !== null;
  useEffect(() => {
    if (!exhausted) return;
    queueMicrotask(() => {
      if (!reloading.current) void showMore();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `showMore` reads this render's state; running out of shown rows, or a re-read settling, is when to read
  }, [exhausted, loadsSettled]);

  /** The row leaves; focus goes to the next row's first action (else the one before, else the heading). */
  function removeRow(key: string, hadFocus: boolean) {
    setState((prev) => {
      if (prev.status !== 'ready') return prev;
      // The rows as they are now, not as they were before the write's await: a Show more that
      // finished meanwhile has its rows here (TMMAPP-0166 item 2, L2).
      const index = prev.page.groups.findIndex((group) => group.key === key);
      const next = prev.page.groups[index + 1] ?? prev.page.groups[index - 1] ?? null;
      if (hadFocus) focusKey.current = next?.key ?? '';
      return {
        status: 'ready',
        page: {
          ...prev.page,
          groups: prev.page.groups.filter((group) => group.key !== key),
          total: Math.max(0, prev.page.total - 1),
        },
      };
    });
    if (editingKey === key) setEditingKey(null);
    onChanged?.();
  }

  /** Runs a row's write; a 404 means it was named or is gone: the row leaves quietly. */
  async function write(key: string, action: () => Promise<string | null>) {
    if (busyKey !== null) return;
    // Read before the row's buttons disable (a disabled button drops the focus).
    const hadFocus =
      rowRefs.current.get(key)?.contains(document.activeElement) === true || editingKey === key;
    setBusyKey(key);
    try {
      const message = await action();
      removeRow(key, hadFocus);
      if (message !== null) onNotify?.(message);
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.PAYEE_TEXT_NOT_FOUND) {
        removeRow(key, hadFocus);
        void load();
      } else {
        // The row stays: its first action takes the focus back.
        if (hadFocus) focusKey.current = key;
        onNotify?.(errorCopy(t, error, 'naming.unnamed.failed'), { severity: 'error' });
      }
    } finally {
      setBusyKey(null);
    }
  }

  function name(group: UnnamedPayeeGroup, body: UnnamedPayeeNameBody) {
    void write(group.key, async () => {
      const result = await unnamedPayeesApi.name(body);
      const payee = result.payee?.name ?? ('name' in body ? body.name : '');
      return result.linesSkipped > 0
        ? t('naming.unnamed.namedSkipped', {
            count: result.linesNamed,
            payee,
            skipped: result.linesSkipped,
          })
        : t('naming.unnamed.named', { count: result.linesNamed, payee });
    });
  }

  function startEdit(group: UnnamedPayeeGroup) {
    setEditingKey(group.key);
    setPick(null);
    setKind(group.kindHint === 'PERSON' ? 'PERSON' : 'VENDOR');
  }

  function save(group: UnnamedPayeeGroup) {
    if (pick === null) return;
    if ('payee' in pick) name(group, { key: group.key, payeeId: pick.payee.id });
    else if (pick.name.trim().length > 0) {
      name(group, { key: group.key, name: pick.name.trim(), kind });
    }
  }

  // One subscriber gate for every row's actions: the button says which (`data-unnamed-action`).
  const act = useSubscriberAction((event: MouseEvent<HTMLButtonElement>) => {
    const { unnamedAction, unnamedKey } = event.currentTarget.dataset;
    const group = groups.find((row) => row.key === unnamedKey);
    if (group === undefined) return;
    if (unnamedAction === 'suggested' && group.suggestedName !== null) {
      name(group, { key: group.key, name: group.suggestedName, kind: kindFor(group) });
    } else if (unnamedAction === 'name') {
      startEdit(group);
    } else if (unnamedAction === 'keep') {
      void write(group.key, async () => {
        await unnamedPayeesApi.keep(group.key);
        return t('naming.unnamed.kept');
      });
    }
  });

  if (state.status === 'loading') {
    return (
      <Skeleton
        variant="rounded"
        height={96}
        role="status"
        aria-label={t('naming.unnamed.titleShort')}
      />
    );
  }
  if (state.status === 'error') {
    return (
      <ErrorState
        variant="section"
        error={state.error}
        what={t('errors.what.unnamedPayees')}
        onRetry={() => void load()}
      />
    );
  }

  const keptLink = keptCount !== null && keptCount > 0 && (
    <Link
      component="button"
      type="button"
      variant="body2"
      data-kept-unnamed-link=""
      onClick={() => setKeptOpen(true)}
      sx={{ alignSelf: 'flex-start' }}
    >
      {t('naming.unnamed.keptLink', { n: keptCount })}
    </Link>
  );
  const keptDialog = keptOpen && (
    <KeptUnnamedDialog
      readOnly={readOnly}
      onRestored={() => {
        onChanged?.();
        void load();
      }}
      onNotify={onNotify}
      onClose={() => setKeptOpen(false)}
    />
  );

  if (!drawn) {
    return (
      <>
        {keptLink}
        {keptDialog}
      </>
    );
  }

  return (
    <Box component="section" aria-labelledby={headingId} data-unnamed-payees="">
      <Stack spacing={1.5}>
        <Typography
          variant="h2"
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          sx={{ outline: 'none' }}
        >
          {t('naming.unnamed.title', { total })}
        </Typography>
        <SubscriberActionHint id={act.descriptionId} description={act.description} />
        <Stack component="ul" spacing={1} aria-labelledby={headingId} sx={{ m: 0, p: 0 }}>
          {groups.map((group) => {
            const editing = editingKey === group.key;
            const busy = busyKey === group.key;
            const sample = group.sample;
            return (
              <Box
                component="li"
                key={group.key}
                ref={(node: HTMLElement | null) => {
                  if (node) rowRefs.current.set(group.key, node);
                  else rowRefs.current.delete(group.key);
                }}
                data-unnamed-row={group.key}
                sx={{
                  listStyle: 'none',
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: (theme: Theme) => theme.palette.divider,
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: editing ? 'column' : 'row' }}
                  sx={{
                    gap: 1.5,
                    alignItems: { md: editing ? 'stretch' : 'center' },
                    justifyContent: 'space-between',
                  }}
                >
                  <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t('naming.fromYourBank')}
                      </Typography>
                      <Typography variant="mono" component="span" sx={{ overflowWrap: 'anywhere' }}>
                        {group.bankText}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {t('naming.unnamed.linesLast', {
                        count: group.lineCount,
                        date: formatCivilDate(group.lastOn, locale),
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      component="div"
                      data-unnamed-sample=""
                      sx={{
                        color: 'text.secondary',
                        display: 'flex',
                        flexWrap: 'wrap',
                        columnGap: 0.75,
                      }}
                    >
                      {sampleParts(group, locale).map((part, index) => (
                        <Box component="span" key={index}>
                          {index > 0 && t('common.listSeparator')}
                          {part === AMOUNT ? (
                            <MoneyText
                              minor={sample.amountMinor}
                              currency={sample.currency}
                              tone="register"
                              variant="body2"
                              component="span"
                            />
                          ) : (
                            part
                          )}
                        </Box>
                      ))}
                    </Typography>
                    {group.aiEvaluated && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('naming.unnamed.aiNoSuggestion')}
                      </Typography>
                    )}
                    {editing && (
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        sx={{ gap: 1.5, pt: 1, alignItems: { sm: 'flex-start' } }}
                        data-unnamed-editor=""
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <PayeePicker
                            label={t('naming.payeeField')}
                            value={pick}
                            onChange={setPick}
                            disabled={busy}
                          />
                        </Box>
                        {pick !== null && 'name' in pick && (
                          <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel id={`${headingId}-kind`}>
                              {t('naming.unnamed.kindLabel')}
                            </InputLabel>
                            <Select
                              labelId={`${headingId}-kind`}
                              label={t('naming.unnamed.kindLabel')}
                              value={kind}
                              disabled={busy}
                              onChange={(event) => setKind(event.target.value as PayeeKind)}
                            >
                              {KINDS.map((value) => (
                                <MenuItem key={value} value={value}>
                                  {t(KIND_KEYS[value])}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Stack>
                    )}
                  </Stack>
                  {!readOnly && (
                    <Stack
                      direction="row"
                      data-unnamed-actions=""
                      sx={{
                        gap: 0.5,
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'flex-start', md: 'flex-end' },
                        flexShrink: 0,
                      }}
                    >
                      {editing ? (
                        <>
                          <SubmitButton
                            size="small"
                            variant="contained"
                            data-unnamed-action="save"
                            loading={busy}
                            disabled={
                              pick === null || ('name' in pick && pick.name.trim().length === 0)
                            }
                            onClick={() => save(group)}
                          >
                            {t('common.save')}
                          </SubmitButton>
                          <Button size="small" disabled={busy} onClick={() => setEditingKey(null)}>
                            {t('common.cancel')}
                          </Button>
                        </>
                      ) : (
                        <>
                          {group.suggestedName !== null && (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={busyKey !== null}
                              data-unnamed-action="suggested"
                              data-unnamed-key={group.key}
                              aria-describedby={act.descriptionId}
                              onClick={act.run}
                            >
                              {t('naming.unnamed.useSuggested', { name: group.suggestedName })}
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant={group.suggestedName === null ? 'contained' : 'outlined'}
                            disabled={busyKey !== null}
                            data-unnamed-action="name"
                            data-unnamed-key={group.key}
                            aria-describedby={act.descriptionId}
                            onClick={act.run}
                          >
                            {t('naming.unnamed.nameIt')}
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            disabled={busyKey !== null}
                            data-unnamed-action="keep"
                            data-unnamed-key={group.key}
                            aria-describedby={act.descriptionId}
                            onClick={act.run}
                          >
                            {t('naming.unnamed.keepUnnamed')}
                          </Button>
                        </>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
        {state.page.nextCursor !== null && (
          <SubmitButton
            variant="outlined"
            size="small"
            loading={loadingMore}
            onClick={() => void showMore()}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('naming.unnamed.showMore')}
          </SubmitButton>
        )}
        {keptLink}
      </Stack>
      {keptDialog}
    </Box>
  );
}

/** A suggested name makes a payee of the hinted kind (a person-to-person rail's), else a business. */
function kindFor(group: UnnamedPayeeGroup): PayeeKind {
  return group.kindHint === 'PERSON' ? 'PERSON' : 'VENDOR';
}

/** A page's groups after the ones already held (none twice), with the later page's total and cursor. */
function appendPage(held: UnnamedPayeePage, next: UnnamedPayeePage): UnnamedPayeePage {
  const seen = new Set(held.groups.map((group) => group.key));
  return {
    groups: [...held.groups, ...next.groups.filter((group) => !seen.has(group.key))],
    total: next.total,
    nextCursor: next.nextCursor,
  };
}

/** Where the sample line's amount goes: a node, never a string. */
const AMOUNT = Symbol('amount');

/** The sample line's parts in order: its date, its amount, its account; absent ones left out. */
function sampleParts(group: UnnamedPayeeGroup, locale: string): Array<string | typeof AMOUNT> {
  const { sample } = group;
  const parts: Array<string | typeof AMOUNT> = [];
  if (sample.postedOn !== null) parts.push(formatCivilDate(sample.postedOn, locale));
  if (sample.amountMinor !== null) parts.push(AMOUNT);
  if (sample.accountName !== null) parts.push(sample.accountName);
  return parts;
}
